/**
 * API Service for ABA Study Buddy Frontend
 *
 * This file contains all HTTP requests to the backend API.
 * It handles:
 * - Authentication (login, register, get current user)
 * - Domain and task list retrieval
 * - Progress tracking and updates
 * - Quiz generation, auto-save, and submission
 * - AI-generated explanations
 *
 * All authenticated requests include JWT token in Authorization header
 */

import axios from "axios";

// Backend URL from environment variable
// Supports either:
// - REACT_APP_BACKEND_URL=https://example.com
// - REACT_APP_BACKEND_URL=https://example.com/api
// and normalizes both to `${origin}/api`
const RAW_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/$/, "").replace(/\/api$/, "");
const API = `${BACKEND_URL}/api`;

/**
 * Get JWT authentication token from localStorage
 * @returns {string|null} JWT token or null if not logged in
 */
const getAuthToken = () => {
  return localStorage.getItem("token");
};

/**
 * Create headers object with JWT token for authenticated requests
 * @returns {Object} Headers object with Authorization header if token exists
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================
// AUTHENTICATION APIs
// ============================================

/**
 * Register a new user account
 * @param {string} firstName - User's first name
 * @param {string} email - User's email address
 * @param {string} password - User's password (min 6 characters)
 * @returns {Promise<Object>} Response with access_token, token_type, and user object
 * @throws {Error} If email already exists or validation fails
 */
export const register = async (firstName, email, password) => {
  const response = await axios.post(`${API}/auth/register`, {
    first_name: firstName,
    email,
    password,
  });
  return response.data;
};

/**
 * Login with existing credentials
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} Response with access_token, token_type, and user object
 * @throws {Error} If credentials are invalid
 */
export const login = async (email, password) => {
  const response = await axios.post(`${API}/auth/login`, {
    email,
    password,
  });
  return response.data;
};

/**
 * Get current logged-in user's information
 * Requires valid JWT token in localStorage
 * @returns {Promise<Object>} User object with id, username, email, created_at
 * @throws {Error} If token is invalid or expired
 */
export const getMe = async () => {
  const response = await axios.get(`${API}/auth/me`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Change current user's password
 * Requires valid JWT token in localStorage
 * @param {string} currentPassword - User's current password
 * @param {string} newPassword - New password meeting policy requirements
 * @returns {Promise<Object>} Confirmation message
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.post(
    `${API}/auth/change-password`,
    {
      current_password: currentPassword,
      new_password: newPassword,
    },
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

// ============================================
// DOMAIN APIs (Public - No auth required)
// ============================================

/**
 * Get all BACB domains with their task items
 * @returns {Promise<Array>} Array of domain objects with items
 * Example response: [{ id: "measurement", name: "Measurement", color: "#3B82F6", items: [...] }]
 */
export const getDomains = async () => {
  const response = await axios.get(`${API}/domains`);
  return response.data;
};

/**
 * Get a specific domain by ID
 * @param {string} domainId - Domain ID (e.g., "measurement", "assessment")
 * @returns {Promise<Object>} Domain object with all task items
 */
export const getDomain = async (domainId) => {
  const response = await axios.get(`${API}/domains/${domainId}`);
  return response.data;
};

// ============================================
// PROGRESS APIs (Authenticated)
// ============================================

/**
 * Get user's overall study progress
 * Requires authentication
 * @returns {Promise<Object>} Progress object with:
 *   - total_items: Total number of task items
 *   - mastered_items: Items with mastery >= 80%
 *   - learning_items: Items with 0% < mastery < 80%
 *   - not_started_items: Items with mastery = 0%
 *   - study_streak: Number of consecutive study days
 *   - total_study_time: Total minutes studied
 *   - item_progress: Object mapping task_code to progress data
 */
export const getProgress = async () => {
  const response = await axios.get(`${API}/progress`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Get progress for a specific task item
 * @param {string} taskCode - Task code (e.g., "A-1", "B-2")
 * @returns {Promise<Object>} Progress object with mastery, review_count, next_review, etc.
 */
export const getTaskProgress = async (taskCode) => {
  const response = await axios.get(`${API}/progress/${taskCode}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

/**
 * Update mastery level after flashcard review
 * Uses spaced repetition algorithm:
 * - "easy" rating: +15% mastery, longer review interval
 * - "hard" rating: -5% mastery, shorter review interval
 *
 * @param {string} taskCode - Task code being reviewed
 * @param {string} rating - Either "easy" or "hard"
 * @returns {Promise<Object>} Updated progress with new_mastery and next_review date
 */
export const updateProgress = async (taskCode, rating) => {
  const response = await axios.post(
    `${API}/progress/update`,
    {
      task_code: taskCode,
      rating: rating,
    },
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

// ============================================
// QUIZ APIs (Authenticated)
// ============================================

/**
 * Generate quiz questions using AI (Claude Sonnet 4)
 * Questions are generated based on BACB task list items for the domain
 * Answer positions are randomized to prevent pattern recognition
 *
 * @param {string} domainId - Domain to generate quiz for
 * @param {number} questionCount - Number of questions (default: 5)
 * @returns {Promise<Object>} Object with questions array
 * Each question includes: id, task_code, question, options (shuffled), correct_answer, explanation
 */
export const generateQuiz = async (domainId, questionCount = 5) => {
  const response = await axios.post(
    `${API}/quiz/generate`,
    {
      domain_id: domainId,
      question_count: questionCount,
    },
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

/**
 * Auto-save a quiz answer during the quiz (called after each question)
 * This ensures answers are preserved even if user closes browser
 *
 * @param {string} quizSessionId - Unique session ID for this quiz
 * @param {string} questionId - ID of the question being answered
 * @param {number} selectedAnswer - Index of selected answer (0-3)
 * @param {boolean} isCorrect - Whether the answer is correct
 * @returns {Promise<Object>} Confirmation message
 */
export const saveQuizAnswer = async (
  quizSessionId,
  questionId,
  selectedAnswer,
  isCorrect,
) => {
  const response = await axios.post(
    `${API}/quiz/save-answer`,
    {
      quiz_session_id: quizSessionId,
      question_id: questionId,
      selected_answer: selectedAnswer,
      is_correct: isCorrect,
    },
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

/**
 * Submit completed quiz and save final results
 * This finalizes the quiz session and stores results in database
 *
 * @param {string} domainId - Domain that was quizzed
 * @param {string} quizSessionId - Session ID for this quiz
 * @param {Array} answers - Array of answer objects with question_id, selected_answer, is_correct
 * @param {string} startTime - ISO timestamp when quiz started
 * @param {string} endTime - ISO timestamp when quiz ended
 * @returns {Promise<Object>} Quiz result with score, correct_answers, time_taken
 */
export const submitQuiz = async (
  domainId,
  quizSessionId,
  answers,
  startTime,
  endTime,
) => {
  const response = await axios.post(
    `${API}/quiz/submit`,
    {
      domain_id: domainId,
      quiz_session_id: quizSessionId,
      answers: answers,
      start_time: startTime,
      end_time: endTime,
    },
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
};

/**
 * Get user's quiz history (all past quiz attempts)
 * Results are sorted by date (newest first)
 *
 * @returns {Promise<Object>} Object with results array
 * Each result includes: id, date, domain, score, total_questions, correct_answers, time_taken
 */
export const getQuizHistory = async () => {
  const response = await axios.get(`${API}/quiz/history`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// ============================================
// EXPLANATION API (Public - No auth required)
// ============================================

/**
 * Generate AI-powered study explanation for a task item
 * Uses Claude to create detailed, formatted explanations with:
 * - Overview, Key Concepts, Examples, Misconceptions, Study Tips
 *
 * @param {string} taskCode - Task code to explain
 * @param {string} taskDescription - Full task description
 * @returns {Promise<Object>} Object with explanation (plain text) and explanation_html (formatted)
 */
export const generateExplanation = async (taskCode, taskDescription) => {
  const response = await axios.post(`${API}/explanation/generate`, {
    task_code: taskCode,
    task_description: taskDescription,
  });
  return response.data;
};
